import React, { useState, useEffect } from 'react';
import { Button, DropdownButton } from '@quantiix/design-system';
import { useTranslation, SUPPORTED_LANGUAGES, i18n } from '../../../i18n';
import { useToast } from '../../../contexts/ToastContext';
import { createLogger } from '../../../utils/logger';
import shared from '../Settings.shared.module.css';
import styles from './LanguageSection.module.css';

const logger = createLogger('Shell::Settings::Language');

function LanguageSection({ settings, editUser }) {
    const { t } = useTranslation('shell');
    const { addToast } = useToast();

    const savedLanguage = settings.language || i18n.language || 'en';

    const [selectedLanguage, setSelectedLanguage] = useState(savedLanguage);
    const [isSaving, setIsSaving] = useState(false);

    // Re-sync if the persisted value changes (initial load + post-save refetch).
    useEffect(() => {
        setSelectedLanguage(settings.language || i18n.language || 'en');
    }, [settings.language]);

    const languageItems = SUPPORTED_LANGUAGES.map(code => ({
        id: code,
        title: t(`languages.${code}`)
    }));

    const selectedLanguageLabel = t(`languages.${selectedLanguage}`);
    const isDirty = selectedLanguage !== savedLanguage;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Persist server-side first; only flip the UI locale once the backend
            // acknowledges so the two stay in sync (and we don't show a language
            // the user hasn't actually saved).
            await editUser({ language: selectedLanguage });
            await i18n.changeLanguage(selectedLanguage);
            logger.info('Language updated', { language: selectedLanguage });
            addToast({
                type: 'success',
                message: t('settings.language.saveSuccess'),
                duration: 3000
            });
        } catch (err) {
            logger.error('Failed to update language', err);
            addToast({
                type: 'error',
                message: err?.message || t('settings.language.saveError'),
                duration: 4000
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={shared.sectionContent}>
            <h3 className={shared.sectionTitle}>{t('settings.language.title')}</h3>
            <div className={styles.languageField}>
                <DropdownButton
                    items={languageItems}
                    onSelect={(item) => setSelectedLanguage(item.id)}
                    variant="tertiary"
                >
                    {selectedLanguageLabel}
                </DropdownButton>
            </div>
            <p className={shared.helperText}>{t('settings.language.helper')}</p>
            <div className={shared.actions}>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    isDisabled={!isDirty || isSaving}
                >
                    {t('settings.language.saveChanges')}
                </Button>
            </div>
        </div>
    );
}

export default LanguageSection;